import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { deletePartnerOffer } from '@/lib/firebase/queries/offers';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import type { PartnerOffer } from './types';

interface DeleteOfferDialogProps {
  offer: PartnerOffer | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function DeleteOfferDialog({ offer, open, onOpenChange, onSuccess }: DeleteOfferDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const { t, dir } = useLanguage();

  const handleDelete = async () => {
    if (!offer) return;

    try {
      setDeleting(true);
      await deletePartnerOffer(offer.id);
      toast.success(t('dashboard.partner.dashboard.offers.delete.success'));
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error(t('dashboard.partner.dashboard.offers.delete.error'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" dir={dir}>
        <DialogHeader>
          <DialogTitle>{t('dashboard.partner.dashboard.offers.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('dashboard.partner.dashboard.offers.delete.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t('dashboard.partner.dashboard.offers.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                {t('dashboard.partner.dashboard.offers.delete.deleting')}
              </>
            ) : (
              t('dashboard.partner.dashboard.offers.delete.confirm')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 